import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ChevronLeft, ChevronRight, Eye, EyeOff, Pencil, Trash2, FlaskConical, Presentation } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";
import RichContent from "@/components/RichContent";
import ContentBlocks from "@/components/ContentBlocks";
import VideoEmbed from "@/components/VideoEmbed";
import { SUBJECTS } from "./Aprende";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

function PracticeItem({ p, index }) {
  const [show, setShow] = useState(false);
  return (
    <div className="bg-white border border-paper-200 border-l-4 border-l-nasa-blue p-6" data-testid={`practice-item-${index}`}>
      <div className="flex items-center gap-2 eyebrow text-nasa-blue mb-3">
        <FlaskConical size={14} /> PROBLEMA {String(index + 1).padStart(2, "0")}
      </div>
      <RichContent html={p.problem} />
      <button
        onClick={() => setShow((s) => !s)}
        data-testid={`toggle-solution-${index}`}
        className="mt-5 inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-full border border-nasa-blue text-nasa-blue hover:bg-nasa-blue hover:text-white transition-colors"
      >
        {show ? <><EyeOff size={15} /> Ocultar solución</> : <><Eye size={15} /> Ver la solución</>}
      </button>
      <AnimatePresence>
        {show && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
            <div className="mt-5 pt-5 border-t border-paper-200 bg-paper-50 -mx-6 -mb-6 px-6 pb-6 pt-5">
              <div className="eyebrow text-nasa-blue mb-2">SOLUCIÓN</div>
              <RichContent html={p.solution} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function Slides({ slides }) {
  const [i, setI] = useState(0);
  if (!slides?.length) return null;
  const s = slides[i];
  return (
    <div data-testid="slides-viewer">
      <div className="relative bg-white border border-paper-200 overflow-hidden min-h-[320px] flex flex-col">
        <div className="flex items-center justify-between px-6 py-3 border-b border-paper-200 bg-nasa-blue text-white">
          <span className="eyebrow flex items-center gap-2"><Presentation size={14} /> SLIDE {i + 1} / {slides.length}</span>
        </div>
        <div className="flex-1 p-8 flex flex-col justify-center">
          {s.title && <h3 className="font-display font-extrabold text-2xl mb-4 text-nasa-blue">{s.title}</h3>}
          {s.image && <img src={mediaUrl(s.image)} alt="" className="max-h-64 mb-4 border border-paper-200 object-contain" />}
          <RichContent html={s.content} />
        </div>
      </div>
      <div className="flex items-center justify-between mt-4">
        <Button variant="outline" className="border-paper-300 rounded-full" disabled={i === 0} onClick={() => setI(i - 1)} data-testid="slide-prev"><ChevronLeft size={16} /> Anterior</Button>
        <div className="flex gap-1.5">
          {slides.map((_, k) => (
            <button key={k} onClick={() => setI(k)} className={`h-2 rounded-full transition-all ${k === i ? "w-6 bg-nasa-blue" : "w-2 bg-paper-300"}`} />
          ))}
        </div>
        <Button variant="outline" className="border-paper-300 rounded-full" disabled={i === slides.length - 1} onClick={() => setI(i + 1)} data-testid="slide-next">Siguiente <ChevronRight size={16} /></Button>
      </div>
    </div>
  );
}

export default function Lesson() {
  const { subject, id } = useParams();
  const meta = SUBJECTS[subject];
  const { isAdmin } = useAuth();
  const nav = useNavigate();
  const [lesson, setLesson] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get(`/lessons/${id}`).then((r) => setLesson(r.data)).catch(() => setErr(true));
  }, [id]);

  const del = async () => {
    try { await api.delete(`/lessons/${id}`); toast.success("Apartado eliminado"); nav(`/aprende/${subject}`); }
    catch { toast.error("No se pudo eliminar"); }
  };

  if (err) return <div className="max-w-3xl mx-auto px-5 py-24 text-center text-ink-500">Apartado no encontrado.</div>;
  if (!lesson) return <div className="max-w-3xl mx-auto px-5 py-24 text-center text-ink-400">Cargando…</div>;

  return (
    <article className="max-w-3xl mx-auto px-5 lg:px-8 pt-24 pb-12">
      <Link to={`/aprende/${subject}`} className="inline-flex items-center gap-2 text-ink-500 hover:text-nasa-blue text-sm font-semibold mb-8" data-testid="back-subject">
        <ArrowLeft size={16} /> {meta?.name || "Volver"}
      </Link>

      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 mb-3"><span className="accent-rule" /><span className="eyebrow text-nasa-blue">{meta?.name?.toUpperCase()} · APARTADO</span></div>
          <h1 className="font-display font-extrabold tracking-tight text-4xl sm:text-5xl leading-[1.05] text-nasa-blue">{lesson.title}</h1>
          {lesson.description && <p className="text-ink-700 mt-4 text-lg">{lesson.description}</p>}
        </div>
        {isAdmin && (
          <div className="flex gap-2 shrink-0">
            <Button variant="outline" size="icon" className="border-paper-300 rounded-full" onClick={() => nav(`/admin/leccion/${id}`)} data-testid="edit-lesson-btn"><Pencil size={16} /></Button>
            <AlertDialog>
              <AlertDialogTrigger asChild><Button variant="outline" size="icon" className="border-nasa-red text-nasa-red hover:bg-nasa-red hover:text-white rounded-full" data-testid="delete-lesson-btn"><Trash2 size={16} /></Button></AlertDialogTrigger>
              <AlertDialogContent className="bg-white">
                <AlertDialogHeader><AlertDialogTitle>¿Eliminar apartado?</AlertDialogTitle><AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription></AlertDialogHeader>
                <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction className="bg-nasa-red hover:bg-nasa-reddark" onClick={del} data-testid="confirm-delete-lesson">Eliminar</AlertDialogAction></AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )}
      </div>

      {lesson.cover_image && <img src={mediaUrl(lesson.cover_image)} alt={lesson.title} className="w-full border border-paper-200 mt-8 object-cover max-h-80" />}

      {lesson.blocks?.length ? (
        <div className="mt-10"><ContentBlocks blocks={lesson.blocks} /></div>
      ) : (
        <>
          {lesson.video_url && (
            <div className="mt-10">
              <div className="flex items-center gap-3 mb-3"><span className="accent-rule" /><span className="eyebrow text-nasa-blue">VÍDEO</span></div>
              <VideoEmbed url={lesson.video_url} />
            </div>
          )}

          {lesson.content && <div className="mt-10"><RichContent html={lesson.content} /></div>}

          {lesson.presentation ? (
            <div className="mt-14" data-testid="presentation-viewer">
              <div className="flex items-center gap-3 mb-5"><span className="accent-rule" /><span className="eyebrow text-nasa-blue">PRESENTACIÓN</span></div>
              <div className="relative w-full h-[420px] sm:h-[560px] border border-paper-200 bg-paper-50">
                <iframe
                  title="Presentación"
                  src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(mediaUrl(lesson.presentation))}`}
                  className="absolute inset-0 w-full h-full"
                  frameBorder="0"
                  allowFullScreen
                />
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2 text-sm text-ink-500">
                <Presentation size={15} className="text-nasa-blue" />
                Usa las flechas del visor para pasar o retroceder las diapositivas.
                <a href={mediaUrl(lesson.presentation)} target="_blank" rel="noreferrer" className="font-semibold text-nasa-blue hover:underline" data-testid="presentation-download">Descargar presentación</a>
              </div>
            </div>
          ) : lesson.slides?.length > 0 ? (
            <div className="mt-14">
              <h2 className="font-display font-extrabold text-2xl mb-5 text-nasa-blue">Diapositivas</h2>
              <Slides slides={lesson.slides} />
            </div>
          ) : null}

          {lesson.practice?.length > 0 && (
            <div className="mt-16">
              <div className="flex items-center gap-3 mb-6 pb-3 border-b-2 border-paper-200">
                <h2 className="font-display font-extrabold text-3xl text-nasa-blue">Practica</h2>
                <span className="eyebrow text-ink-400">{lesson.practice.length} PROBLEMAS</span>
              </div>
              <div className="space-y-5">
                {lesson.practice.map((p, i) => <PracticeItem key={p.id || i} p={p} index={i} />)}
              </div>
            </div>
          )}
        </>
      )}
    </article>
  );
}
